<template>
  <div class="searchable-select-container" :class="$attrs.class" :style="$attrs.style">
    <!-- Input Label -->
    <label v-if="inputLabel" :for="computedId" class="form-label">
      {{ inputLabel }}
      <span v-if="required" class="text-danger">*</span>
    </label>

    <!-- Vueform Multiselect -->
    <Multiselect
      v-model="innerValue"
      v-bind="filteredAttrs"
      :id="computedId"
      :options="computedOptions"
      :searchable="searchable"
      :placeholder="placeholder"
      :mode="mode"
      :close-on-select="closeOnSelect"
      :create-option="createOption"
      :disabled="disabled"
      :loading="loading"
      :value-prop="valueProp"
      :label="label"
      :track-by="trackBy"
      :can-clear="canClear"
      :can-deselect="canDeselect"
      :hide-selected="hideSelected"
      :class="[selectClass, `searchable-select-${size}`, { 'is-invalid': !!error }]"
      @change="handleChange"
    >
      <!-- Forward all slots (except default) to the underlying Multiselect component -->
      <template v-for="(_, name) in filteredSlots" :key="name" v-slot:[name]="slotData">
        <slot :name="name" v-bind="slotData || {}" />
      </template>
    </Multiselect>

    <!-- Error/Validation Message -->
    <div v-if="error" class="invalid-feedback d-block">
      {{ error }}
    </div>
  </div>
</template>

<script>
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";

export default {
  name: "SearchableSelect",
  components: {
    Multiselect,
  },
  inheritAttrs: false,
  props: {
    // v-model binding
    modelValue: {
      type: [String, Number, Array, Object],
      default: null,
    },
    // List of select options
    options: {
      type: [Array, Object, Function],
      default: () => [],
    },
    // Toggle search functionality
    searchable: {
      type: Boolean,
      default: true,
    },
    // Placeholder text
    placeholder: {
      type: String,
      default: "Select option",
    },
    // Mode of selection: "single", "multiple", or "tags"
    mode: {
      type: String,
      default: "single",
    },
    // Automatically close dropdown on selection
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
    // Allow users to create custom options on the fly
    createOption: {
      type: Boolean,
      default: false,
    },
    // Disable the select dropdown
    disabled: {
      type: Boolean,
      default: false,
    },
    // Show a loading spinner
    loading: {
      type: Boolean,
      default: false,
    },
    // Property key in the option object to be used as value
    valueProp: {
      type: String,
      default: "value",
    },
    // Property key in the option object to be used as label
    label: {
      type: String,
      default: "label",
    },
    // Property key to track options by
    trackBy: {
      type: String,
      default: "label",
    },
    // Allow clearing the selection
    canClear: {
      type: Boolean,
      default: true,
    },
    // Allow deselecting an option in single-select mode
    canDeselect: {
      type: Boolean,
      default: true,
    },
    // Hide selected options from the dropdown list
    hideSelected: {
      type: Boolean,
      default: true,
    },
    // Validation error message text
    error: {
      type: String,
      default: null,
    },
    // Label for the select input element
    inputLabel: {
      type: String,
      default: null,
    },
    // Show required asterisk indicator
    required: {
      type: Boolean,
      default: false,
    },
    // Unique ID for the input element
    id: {
      type: String,
      default: null,
    },
    // Custom class for the underlying Multiselect component (e.g. Bootstrap utility classes)
    selectClass: {
      type: [String, Object, Array],
      default: null,
    },
    // Size of the select: "xs", "sm", "md", "lg", "xl", or "xxl"
    size: {
      type: String,
      default: "sm",
      validator: (value) => ["xs", "sm", "md", "lg", "xl", "xxl"].includes(value),
    },
  },
  emits: ["update:modelValue", "change"],
  computed: {
    innerValue: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      },
    },
    computedId() {
      return this.id || `searchable-select-${Math.random().toString(36).substring(2, 9)}`;
    },
    filteredAttrs() {
      // Clone $attrs and delete class, style, and id to prevent duplicated binding on multiselect
      const attrs = { ...this.$attrs };
      delete attrs.class;
      delete attrs.style;
      delete attrs.id;
      return attrs;
    },
    filteredSlots() {
      // Exclude default slot from being forwarded since we parse it for options
      const slots = { ...this.$slots };
      delete slots.default;
      return slots;
    },
    computedOptions() {
      // Prioritize the options prop if it is provided and not empty
      const hasOptionsProp = this.options && (
        Array.isArray(this.options)
          ? this.options.length > 0
          : (typeof this.options === 'function' || Object.keys(this.options).length > 0)
      );

      if (hasOptionsProp) {
        return this.options;
      }

      // Otherwise, parse options dynamically from the default slot (<option> tags)
      return this.getOptionsFromSlots();
    },
  },
  methods: {
    handleChange(value) {
      this.$emit("change", value);
    },
    getOptionsFromSlots() {
      if (!this.$slots.default) {
        return [];
      }

      try {
        const nodes = this.$slots.default();
        const parsed = [];

        const parseNode = (node) => {
          if (!node) return;

          // Handle Fragment wrapper (such as when using v-for)
          if (node.type && (typeof node.type === "symbol" || node.type.toString().includes("Fragment"))) {
            if (Array.isArray(node.children)) {
              node.children.forEach(parseNode);
            }
          } else if (node.type === "option") {
            const props = node.props || {};
            const value = props.value !== undefined ? props.value : props.Value;

            let label = "";
            if (typeof node.children === "string") {
              label = node.children;
            } else if (Array.isArray(node.children)) {
              label = node.children
                .map((c) => {
                  if (typeof c === "string") return c;
                  if (c && typeof c === "object" && c.children) {
                    return typeof c.children === "string" ? c.children : "";
                  }
                  return "";
                })
                .join("")
                .trim();
            }

            parsed.push({
              value: value,
              label: label || String(value),
            });
          }
        };

        nodes.forEach(parseNode);
        return parsed;
      } catch (err) {
        console.error("Error parsing slots for SearchableSelect options:", err);
        return [];
      }
    },
  },
};
</script>

<style scoped>
.searchable-select-container {
  position: relative;
  width: 100%;
}

/* Sizes */
:deep(.searchable-select-xs) {
  --ms-min-height: 26px;
  --ms-font-size: 0.75rem; /* 12px */
  --ms-py: 1px;
  --ms-px: 6px;
  min-height: 26px !important;
}
:deep(.searchable-select-xs .multiselect-wrapper) {
  min-height: 26px !important;
}
:deep(.searchable-select-xs .multiselect-option) {
  font-size: 0.75rem;
  padding: 4px 8px;
}

:deep(.searchable-select-sm) {
  --ms-min-height: 31px;
  --ms-font-size: 0.8125rem; /* ~13px */
  --ms-py: 3px;
  --ms-px: 8px;
  min-height: 31px !important;
}
:deep(.searchable-select-sm .multiselect-wrapper) {
  min-height: 31px !important;
}
:deep(.searchable-select-sm .multiselect-option) {
  font-size: 0.8125rem;
  padding: 6px 10px;
}

:deep(.searchable-select-md) {
  --ms-min-height: 38px;
  --ms-font-size: 0.875rem;
  --ms-py: 5px;
  --ms-px: 12px;
  min-height: 38px !important;
}
:deep(.searchable-select-md .multiselect-wrapper) {
  min-height: 38px !important;
}
:deep(.searchable-select-md .multiselect-option) {
  font-size: 0.875rem;
  padding: 8px 12px;
}

:deep(.searchable-select-lg) {
  --ms-min-height: 48px;
  --ms-font-size: 1.125rem;
  --ms-py: 8px;
  --ms-px: 16px;
  min-height: 48px !important;
}
:deep(.searchable-select-lg .multiselect-wrapper) {
  min-height: 48px !important;
}
:deep(.searchable-select-lg .multiselect-option) {
  font-size: 1.125rem;
  padding: 10px 16px;
}

:deep(.searchable-select-xl) {
  --ms-min-height: 56px;
  --ms-font-size: 1.25rem; /* 20px */
  --ms-py: 12px;
  --ms-px: 20px;
  min-height: 56px !important;
}
:deep(.searchable-select-xl .multiselect-wrapper) {
  min-height: 56px !important;
}
:deep(.searchable-select-xl .multiselect-option) {
  font-size: 1.25rem;
  padding: 12px 20px;
}

:deep(.searchable-select-xxl) {
  --ms-min-height: 64px;
  --ms-font-size: 1.5rem; /* 24px */
  --ms-py: 16px;
  --ms-px: 24px;
  min-height: 64px !important;
}
:deep(.searchable-select-xxl .multiselect-wrapper) {
  min-height: 64px !important;
}
:deep(.searchable-select-xxl .multiselect-option) {
  font-size: 1.5rem;
  padding: 16px 24px;
}

/* Deep border styles for invalid state */
:deep(.multiselect.is-invalid) {
  border-color: #f06548 !important;
}

:deep(.multiselect.is-invalid:focus-within) {
  box-shadow: 0 0 0 0.2rem rgba(240, 101, 72, 0.25) !important;
}

.invalid-feedback {
  color: #f06548;
  font-size: 0.875em;
  margin-top: 0.25rem;
}
</style>
